/**
 * Client-side entitlement claim sync helpers.
 *
 * The backend no longer revokes refresh tokens when it changes a user's
 * entitlements (revocation silently logged buyers out ~60 min after purchase —
 * the buyer-logout P1 fix). Instead it writes the entitlements to the Firestore
 * user doc, and the client force-refreshes its ID token when that mirror drifts
 * from the entitlements the current token carries. `shouldForceRefreshClaims`
 * is the pure decision function behind the `useClaimSync` hook.
 */

function toEntitlementSet(value) {
  if (!Array.isArray(value)) return new Set();
  return new Set(value.filter((e) => typeof e === "string"));
}

function sameSet(a, b) {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

/**
 * Decide whether the client should call getIdToken(true) to pull fresh claims.
 *
 * @param {object} args
 * @param {string[]|null|undefined} args.tokenEntitlements - entitlements from the current ID token
 * @param {string[]|null|undefined} args.docEntitlements - entitlements mirrored on the Firestore user doc
 * @returns {boolean} true when the sets differ (token is stale relative to the doc)
 */
export function shouldForceRefreshClaims({ tokenEntitlements, docEntitlements } = {}) {
  const tokenSet = toEntitlementSet(tokenEntitlements);
  const docSet = toEntitlementSet(docEntitlements);
  return !sameSet(tokenSet, docSet);
}

/**
 * Reconcile the current ID token against the doc's entitlement mirror,
 * force-refreshing the token when they drift. Reads the token's claims via
 * getIdTokenResult() (cached, cheap) and only calls getIdToken(true) when a
 * refresh is actually needed. Safe to call repeatedly: once the forced refresh
 * lands, the token matches the doc and subsequent calls are no-ops.
 *
 * @param {object} args
 * @param {import('firebase/auth').User|null|undefined} args.user - the current Firebase user
 * @param {string[]|null|undefined} args.docEntitlements - entitlements mirrored on the user doc
 * @returns {Promise<boolean>} true if a forced refresh was performed
 */
export async function reconcileClaims({ user, docEntitlements }) {
  if (!user || typeof user.getIdTokenResult !== "function") return false;

  const result = await user.getIdTokenResult();
  const tokenEntitlements = result && result.claims ? result.claims.entitlements : undefined;

  if (!shouldForceRefreshClaims({ tokenEntitlements, docEntitlements })) return false;

  await user.getIdToken(true);
  return true;
}
